import { useEffect } from 'react';

/**
 * N8 (86d3w2kez) part 3 — "is the user already looking at this?".
 *
 * A posting surface registers itself while it is mounted, and `notify()` stays quiet for outcomes
 * belonging to a record whose own page is open and focused. Telling someone their sales order posted,
 * with a toast, while they are watching the card that just said so is noise — and it trains people to
 * dismiss toasts unread, which costs them the one that mattered.
 *
 * <b>A module-scoped Set rather than a store</b>, because nothing renders from it. It is asked a
 * question at the moment a frame arrives and never subscribed to; a zustand store would add a
 * subscription surface for state no component reads.
 *
 * ⚠️ FOCUS IS PART OF THE CONDITION. A page open in a background tab is not being watched — the
 * browser reports `document.hidden`, and suppressing there would silently drop the notification for a
 * user who is somewhere else entirely.
 */
const present = new Set<string>();

function keyOf(type: string, entityId: string | number): string {
  return `${type}:${entityId}`;
}

/**
 * Declare that this page is showing the posting surface for one record.
 *
 * Pass a null `entityId` while the page is still loading — the registration simply does not happen,
 * which fails toward SHOWING the toast. That is the safe direction: a redundant toast is mildly
 * annoying, a swallowed one loses the user a failed posting.
 */
export function usePostingSurfacePresence(type: string, entityId: string | number | null): void {
  useEffect(() => {
    if (entityId === null || entityId === '') return;

    const key = keyOf(type, entityId);
    present.add(key);
    return () => {
      present.delete(key);
    };
  }, [type, entityId]);
}

/**
 * Is this record's own surface open AND in front of the user right now?
 *
 * ⚠️ Read at the moment the frame lands, never cached. A user who alt-tabs away between the post and
 * its outcome must still be told.
 */
export function isPostingSurfaceWatched(type: string, entityId: string | null): boolean {
  if (!entityId) return false;
  if (typeof document !== 'undefined' && document.hidden) return false;

  return present.has(keyOf(type, entityId));
}

/** Test seam — the registry is module state, so a suite has to be able to clear it. */
export function __resetPostingSurfacePresence(): void {
  present.clear();
}
